<template>
	<div id="hero-top" class="jet-landing-root">
		<!-- ========================================================================= -->
		<!-- 1. MODERN HERO BANNER WITH 3D MICROINTERACTION & PARTICLES                -->
		<!-- ========================================================================= -->
		<section ref="heroBanner" class="hero-section" @mousemove="onHeroMouseMove" @mouseleave="onHeroMouseLeave">
			<!-- Ambient lighting & spotlight glow that follows cursor -->
			<div class="hero-spotlight-glow" :style="{
				background: `radial-gradient(circle 600px at ${spotlightX}% ${spotlightY}%, rgba(82, 46, 168, 0.18), rgba(255, 152, 0, 0.08) 45%, transparent 75%)`
			}"></div>
			<div class="ambient-orb orb-1"></div>
			<div class="ambient-orb orb-2"></div>
			<div class="ambient-grid-pattern"></div>

			<v-container class="hero-container py-10 py-md-16">
				<v-row class="align-center">
					<!-- RIGHT COLUMN (in RTL): Hero Visual Mockup & Interactive 3D Card -->
					<v-col cols="12" lg="6" class="order-1 order-lg-1">
						<div class="hero-mockup-wrapper">
							<!-- 3D Perspective Card -->
							<div class="hero-3d-card" :style="{
								transform: `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale3d(1.02, 1.02, 1.02)`
							}">
								<!-- Mockup Window Bar -->
								<div class="mockup-header-bar">
									<div class="mockup-dot-group">
										<span class="m-dot dot-red"></span>
										<span class="m-dot dot-yellow"></span>
										<span class="m-dot dot-green"></span>
									</div>
									<div class="mockup-title-text">
										<v-icon small color="#ff9800" class="ml-1">mdi-flash</v-icon>
										<span>سامانه هوشمند جت اقساط | داشبورد فروشندگان</span>
									</div>
									<div class="mockup-live-status">
										<span class="live-dot"></span>
										<span class="live-label">آنلاین و ابری</span>
									</div>
								</div>

								<!-- Mockup Screen Body -->
								<div class="mockup-image-container">
									<img :src="require('@/assets/banner-jet-aghsat.jpg')" alt="پیش‌نمایش سامانه جت اقساط" class="mockup-screen-img" />
									<div class="mockup-sheen" :style="{
										background: `linear-gradient(${120 + tiltY * 2.5}deg, rgba(255, 255, 255, 0.28) 0%, transparent 60%)`
									}"></div>

									<!-- Quick Floating Action Overlay in Mockup -->
									<div class="mockup-floating-pill">
										<v-icon small color="#2e7d32" class="ml-1">mdi-check-decagram</v-icon>
										<span>رتبه اعتباری مشتری: تایید شد (A+)</span>
									</div>
								</div>
							</div>

							<!-- Floating Microinteraction Badges -->
							<div class="floating-badge badge-credit" :style="{
								transform: `translate3d(${badgeX1}px, ${badgeY1}px, 40px)`
							}">
								<div class="badge-icon-box bg-purple">
									<v-icon color="#ffffff" small>mdi-timer-sand-complete</v-icon>
								</div>
								<div class="badge-body">
									<span class="badge-bold">استعلام در ۲ دقیقه</span>
									<span class="badge-sub">بدون چک برگشتی</span>
								</div>
							</div>

							<div class="floating-badge badge-sign" :style="{
								transform: `translate3d(${badgeX2}px, ${badgeY2}px, 50px)`
							}">
								<div class="badge-icon-box bg-emerald">
									<v-icon color="#ffffff" small>mdi-draw-pen</v-icon>
								</div>
								<div class="badge-body">
									<span class="badge-bold">امضای دیجیتال موبایل</span>
									<span class="badge-sub">قرارداد رسمی و معتبر</span>
								</div>
							</div>

							<div class="floating-badge badge-sms" :style="{
								transform: `translate3d(${badgeX3}px, ${badgeY3}px, 45px)`
							}">
								<div class="badge-icon-box bg-orange">
									<v-icon color="#ffffff" small>mdi-message-cog</v-icon>
								</div>
								<div class="badge-body">
									<span class="badge-bold">پیامک خودکار</span>
									<span class="badge-sub">با لینک مستقیم پرداخت</span>
								</div>
							</div>
						</div>
					</v-col>

					<!-- LEFT COLUMN (in RTL): Headline, Subtitle, 3 CTA Buttons -->
					<v-col cols="12" lg="6" class="order-2 order-lg-2 pr-lg-8">
						<div class="hero-content-box">
							<!-- Main Headline (تیتر اصلی) -->
							<h1 class="hero-main-title mb-4">
								فروش قسطی بدون دنبال مشتری دویدن
							</h1>

							<!-- Subtitle (زیرتیتر) -->
							<p class="hero-main-subtitle mb-6 ">
								<span class="sub-feature-pill">
									<v-icon x-small color="#ff9800" class="ml-1">mdi-clock-check-outline</v-icon>
									استعلام اعتبار در ۲ دقیقه
								</span>
								<span class="sub-separator">+</span>
								<span class="sub-feature-pill">
									<v-icon x-small color="#522ea8" class="ml-1">mdi-file-certificate-outline</v-icon>
									قرارداد چاپی و امضای دیجیتال
								</span>
								<span class="sub-separator">+</span>
								<span class="sub-feature-pill">
									<v-icon x-small color="#2e7d32" class="ml-1">mdi-message-check-outline</v-icon>
									یادآوری خودکار پیامکی و ایمیلی
								</span>
								<span class="sub-separator">+</span>
								<span class="sub-feature-pill">
									<v-icon x-small color="#0091ea" class="ml-1">mdi-credit-card-outline</v-icon>
									لینک پرداخت آنلاین برای مشتری
								</span>
							</p>

							<!-- 3 Primary Action Buttons (۳ دکمه زیر تیتر) -->
							<div class="hero-cta-buttons mb-8">
								<!-- Button 1: WhatsApp Consultation & Demo -->
								<v-btn large elevation="4" rounded class="cta-btn btn-whatsapp mb-3" :href="whatsappUrl" target="_blank">
									<div class="btn-pulse-wave"></div>
									<v-icon left size="22" color="#ffffff">mdi-whatsapp</v-icon>
									<span class="btn-text">درخواست دمو و مشاوره رایگان در واتساپ</span>
								</v-btn>

								<div class="d-flex flex-wrap gap-3">
									<!-- Button 2: Jet Aghsat in 2 Minutes (Aparat Video) -->
									<v-btn large elevation="3" rounded class="cta-btn btn-video flex-grow-1 ml-1" @click="openVideoModal">
										<v-icon left size="22" color="#ff9800">mdi-play-circle</v-icon>
										<span class="btn-text">معرفی جت اقساط در 2 دقیقه</span>
									</v-btn>

									<!-- Button 3: Free Migration from Excel & Ledger -->
									<v-btn large outlined rounded class="cta-btn btn-excel flex-grow-1" @click="openExcelModal">
										<v-icon left size="22" color="#2e7d32">mdi-file-excel-box</v-icon>
										<span class="btn-text">انتقال رایگان دفتر و اکسل شما به سیستم</span>
									</v-btn>
								</div>
							</div>

						</div>
					</v-col>
				</v-row>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- 2. FOUR BENEFITS COLUMNS IN BAZAAR LANGUAGE (۴ ستون مزیت با زبان صنف)     -->
		<!-- ========================================================================= -->
		<section id="benefits" class="landing-section benefits-bazaar-section">
			<v-container style="max-width: 1240px;">
				<div class="section-badge-header text-center mb-12">
					<div class="section-chip mb-2">
						<v-icon small color="#ff9800">mdi-storefront-outline</v-icon>
						<span>تجربه اصناف و بازاریان</span>
					</div>
					<h2 class="section-main-heading">
						چهار مرحله فروش اقساطی امن
					</h2>
					<p class="section-sub-heading">
						از لحظه ورود مشتری تا واریز آخرین ریال قسط؛ بدون استرس چک برگشتی و تعارف با خریدار
					</p>
				</div>

				<v-row class="justify-center align-stretch">
					<!-- Column 1: قبل از فروش -->
					<v-col cols="12" sm="6" lg="3">
						<div class="bazaar-column-card card-stage-1">
							<div class="stage-top-indicator">
								<span class="stage-step-num">۱</span>
								<span class="stage-pill-tag">قبل از فروش</span>
							</div>

							<div class="stage-icon-circle bg-stage-1">
								<v-icon size="36" color="#d32f2f">mdi-shield-alert-outline</v-icon>
							</div>

							<h3 class="stage-card-title">قبل از فروش</h3>
							<p class="stage-bazaar-slogan">
								«اتصال به سامانه استعلام و رتبه اعتباری، دیگه به آدم بدحساب جنس نده»
							</p>

							<div class="stage-features-list">
								<div class="stage-feature-item">
									<v-icon x-small color="#d32f2f">mdi-check-bold</v-icon>
									<span>استعلام رتبه اعتباری و سابقه چک‌های برگشتی در ۲ دقیقه</span>
								</div>
								<div class="stage-feature-item">
									<v-icon x-small color="#d32f2f">mdi-check-bold</v-icon>
									<span>شناسایی بدحساب‌ها قبل از تحویل کالا و سوزاندن سرمایه</span>
								</div>
								<div class="stage-feature-item">
									<v-icon x-small color="#d32f2f">mdi-check-bold</v-icon>
									<span>پایان دوندگی‌های طاقت‌فرسا در دادگاه و شعب شورا</span>
								</div>
							</div>

							<div class="stage-card-footer">
								<v-chip x-small color="red lighten-5" text-color="#c62828" class="font-weight-bold">
									<v-icon left x-small>mdi-lock-check</v-icon>
									بیمه سرمایه در لحظه خرید
								</v-chip>
							</div>
						</div>
					</v-col>

					<!-- Column 2: حین فروش -->
					<v-col cols="12" sm="6" lg="3">
						<div class="bazaar-column-card card-stage-2">
							<div class="stage-top-indicator">
								<span class="stage-step-num">۲</span>
								<span class="stage-pill-tag">حین فروش</span>
							</div>

							<div class="stage-icon-circle bg-stage-2">
								<v-icon size="36" color="#522ea8">mdi-draw-pen</v-icon>
							</div>

							<h3 class="stage-card-title">حین فروش</h3>
							<p class="stage-bazaar-slogan">
								«چاپ قرارداد + امضای دیجیتال روی موبایل، معتبر و بدون کاغذبازی»
							</p>

							<div class="stage-features-list">
								<div class="stage-feature-item">
									<v-icon x-small color="#522ea8">mdi-check-bold</v-icon>
									<span>چاپ فوری قرارداد رسمی، قانونی و شفاف صنف با یک کلیک</span>
								</div>
								<div class="stage-feature-item">
									<v-icon x-small color="#522ea8">mdi-check-bold</v-icon>
									<span>امضای دیجیتال با اتصال به سامانه های مورد تایید قوه قضائیه</span>
								</div>
								<div class="stage-feature-item">
									<v-icon x-small color="#522ea8">mdi-check-bold</v-icon>
									<span>ارسال پیامک تاییدیه امنیتی با توکن otp</span>
								</div>
							</div>

							<div class="stage-card-footer">
								<v-chip x-small color="purple lighten-5" text-color="#522ea8" class="font-weight-bold">
									<v-icon left x-small>mdi-certificate</v-icon>
									مورد تایید و رسمی
								</v-chip>
							</div>
						</div>
					</v-col>

					<!-- Column 3: بعد از فروش -->
					<v-col cols="12" sm="6" lg="3">
						<div class="bazaar-column-card card-stage-3">
							<div class="stage-top-indicator">
								<span class="stage-step-num">۳</span>
								<span class="stage-pill-tag">بعد از فروش</span>
							</div>

							<div class="stage-icon-circle bg-stage-3">
								<v-icon size="36" color="#f57c00">mdi-message-cog-outline</v-icon>
							</div>

							<h3 class="stage-card-title">بعد از فروش</h3>
							<p class="stage-bazaar-slogan">
								«پیامک خودکار قبل از موعد، روز سررسید، معوق و تشکر از پرداخت با متن دلخواه خودت»
							</p>

							<div class="stage-features-list">
								<div class="stage-feature-item">
									<v-icon x-small color="#f57c00">mdi-check-bold</v-icon>
									<span>ارسال خودکار پیامک یادآوری هوشمند ۳ روز قبل و روز موعد</span>
								</div>
								<div class="stage-feature-item">
									<v-icon x-small color="#f57c00">mdi-check-bold</v-icon>
									<span>ارسال مستقیم لینک پرداخت آنلاین داخل هر پیامک</span>
								</div>
								<div class="stage-feature-item">
									<v-icon x-small color="#f57c00">mdi-check-bold</v-icon>
									<span>ارسال خودکار پیامک تشکر و رسید الکترونیک پس از واریز</span>
								</div>
							</div>

							<div class="stage-card-footer">
								<v-chip x-small color="orange lighten-5" text-color="#e65100" class="font-weight-bold">
									<v-icon left x-small>mdi-cellphone-message</v-icon>
									اتصال به سامانه پیامکی
								</v-chip>
							</div>
						</div>
					</v-col>

					<!-- Column 4: وصول پول -->
					<v-col cols="12" sm="6" lg="3">
						<div class="bazaar-column-card card-stage-4">
							<div class="stage-top-indicator">
								<span class="stage-step-num">۴</span>
								<span class="stage-pill-tag">وصول پول</span>
							</div>

							<div class="stage-icon-circle bg-stage-4">
								<v-icon size="36" color="#2e7d32">mdi-credit-card-fast-outline</v-icon>
							</div>

							<h3 class="stage-card-title">وصول پول</h3>
							<p class="stage-bazaar-slogan">
								«پنل جدا برای مشتری + مدیریت نمایندهها و پذیرندهها، هرکی قسطش را آنلاین بدهد»
							</p>

							<div class="stage-features-list">
								<div class="stage-feature-item">
									<v-icon x-small color="#2e7d32">mdi-check-bold</v-icon>
									<span>پرتال اختصاصی مشتری برای پرداخت ۲۴ ساعته بدون کارت‌به‌کارت</span>
								</div>
								<div class="stage-feature-item">
									<v-icon x-small color="#2e7d32">mdi-check-bold</v-icon>
									<span>کسر خودکار قسط و به‌روزرسانی مانده بدهی بدون دخالت شما</span>
								</div>
								<div class="stage-feature-item">
									<v-icon x-small color="#2e7d32">mdi-check-bold</v-icon>
									<span>مدیریت پذیرنده‌ها و چند شعبه در یک داشبورد با دسترسی مجزا</span>
								</div>
							</div>

							<div class="stage-card-footer">
								<v-chip x-small color="green lighten-5" text-color="#1b5e20" class="font-weight-bold">
									<v-icon left x-small>mdi-cash-check</v-icon>
									تسویه آنی و شفاف
								</v-chip>
							</div>
						</div>
					</v-col>
				</v-row>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- 3. SPONSOR / INVESTOR SECTION (اگر سرمایه‌گذار اقساطی هستی)                 -->
		<!-- ========================================================================= -->
		<section id="sponsor" class="landing-section sponsor-vip-section">
			<!-- Background luxury glowing elements -->
			<div class="sponsor-orb orb-gold"></div>
			<div class="sponsor-orb orb-dark-purple"></div>

			<v-container style="max-width: 1240px;" class="position-relative">
				<div class="sponsor-header text-center mb-12">
					<div class="sponsor-chip mb-3">
						<v-icon small color="#ffb74d">mdi-diamond-stone</v-icon>
						<span>فرصت ویژه سرمایه‌گذاری و مشارکت در تسهیلات</span>
					</div>
					<h2 class="sponsor-main-title">
						اگر سرمایه‌گذار اقساطی هستی
					</h2>
					<p class="sponsor-sub-title">
						جریان نقدینگی و سود سرمایه‌ات را بدون ریسک، بدون خطای دفتری و با حداکثر شفافیت در یک نگاه کنترل کن
					</p>
				</div>

				<v-row class="justify-center align-stretch">
					<!-- Bullet 1: گزارش سود شفاف -->
					<v-col cols="12" md="4">
						<div class="sponsor-feature-card">
							<div class="sponsor-icon-wrapper">
								<v-icon size="40" color="#ffb74d">mdi-chart-areaspline</v-icon>
							</div>
							<h3 class="sponsor-card-heading">گزارش سود شفاف</h3>
							<p class="sponsor-card-desc">
								تفکیک دقیق و لحظه‌ای اصل سرمایه، سود ماهانه، جرایم دیرکرد و پاداش تسویه‌های زودهنگام به صورت تفکیک‌شده؛ بدون نیاز به ساعت‌ها وقت گذاشتن روی دفترچه‌ها یا خطاهای حسابداری دستی.
							</p>
							<div class="sponsor-bullet-features">
								<div class="b-line">
									<v-icon x-small color="#ffb74d">mdi-check-circle</v-icon>
									<span>نمایش دقیق سود خالص هر پرونده</span>
								</div>
								<div class="b-line">
									<v-icon x-small color="#ffb74d">mdi-check-circle</v-icon>
									<span>پیش‌بینی ورود نقدینگی ماه‌های آتی</span>
								</div>
								<div class="b-line">
									<v-icon x-small color="#ffb74d">mdi-check-circle</v-icon>
									<span>خروجی اکسل کامل برای حسابرسان</span>
								</div>
							</div>
						</div>
					</v-col>

					<!-- Bullet 2: رتبه ریسک هر مشتری -->
					<v-col cols="12" md="4">
						<div class="sponsor-feature-card featured-sponsor-card">
							<div class="featured-tag">حیاتی برای سرمایه‌گذار</div>
							<div class="sponsor-icon-wrapper">
								<v-icon size="40" color="#ff9800">mdi-shield-crown-outline</v-icon>
							</div>
							<h3 class="sponsor-card-heading">رتبه ریسک هر مشتری</h3>
							<p class="sponsor-card-desc">
								بررسی پیشینه اعتباری، بدحسابی‌های قبلی و سابقه چک‌های بانکی هر متقاضی وام قبل از تخصیص سرمایه؛ سرمایه‌ات را فقط به مشتریان خوش‌حساب و پرونده‌های تضمین‌شده اختصاص بده.
							</p>
							<div class="sponsor-bullet-features">
								<div class="b-line">
									<v-icon x-small color="#ff9800">mdi-check-circle</v-icon>
									<span>سنجش احتمال نکول و تاخیر هر خریدار</span>
								</div>
								<div class="b-line">
									<v-icon x-small color="#ff9800">mdi-check-circle</v-icon>
									<span>اعتبارسنجی آنلاین خریدار و ضامن</span>
								</div>
								<div class="b-line">
									<v-icon x-small color="#ff9800">mdi-check-circle</v-icon>
									<span>کاهش نرخ مطالبات معوق به زیر ۱٪</span>
								</div>
							</div>
						</div>
					</v-col>

					<!-- Bullet 3: مدیریت همه پذیرنده‌ها در یک داشبورد -->
					<v-col cols="12" md="4">
						<div class="sponsor-feature-card">
							<div class="sponsor-icon-wrapper">
								<v-icon size="40" color="#ffb74d">mdi-view-dashboard-variant-outline</v-icon>
							</div>
							<h3 class="sponsor-card-heading">مدیریت همه پذیرنده‌ها در یک داشبورد</h3>
							<p class="sponsor-card-desc">
								اگر با ده‌ها فروشگاه، مغازه و نماینده مختلف کار می‌کنی، تمام آنها را در یک پنل متمرکز مدیریت کن؛ سهم سود هر پذیرنده خودکار محاسبه شده و حساب‌کتاب‌ها کاملاً شفاف است.
							</p>
							<div class="sponsor-bullet-features">
								<div class="b-line">
									<v-icon x-small color="#ffb74d">mdi-check-circle</v-icon>
									<span>مدیریت چند شعبه و نماینده با یک اکانت</span>
								</div>
								<div class="b-line">
									<v-icon x-small color="#ffb74d">mdi-check-circle</v-icon>
									<span>محاسبه خودکار کارمزد و پورسانت پذیرنده</span>
								</div>
								<div class="b-line">
									<v-icon x-small color="#ffb74d">mdi-check-circle</v-icon>
									<span>تسویه حساب امن و اتوماتیک با پذیرندگان</span>
								</div>
							</div>
						</div>
					</v-col>
				</v-row>

				<!-- Sponsor Action Banner -->
				<div class="sponsor-cta-banner mt-10">
					<div class="d-flex flex-column flex-md-row align-center justify-space-between pa-6 rounded-2xl">
						<div class="sponsor-cta-text mb-4 mb-md-0">
							<h4 class="text-h6 font-weight-black white--text mb-1">می‌خواهی سرمایه‌ات را در اقساط اصناف با جت اقساط مدیریت کنی؟</h4>
							<p class="mb-0 text-body-2" style="color: rgba(255, 255, 255, 0.75);">
								تیم تخصصی جت اقساط برای راه‌اندازی پنل اختصاصی سرمایه‌گذاران و تنظیم مدل مالی در کنار شماست.
							</p>
						</div>
						<v-btn large rounded elevation="4" color="#ff9800" dark class="font-weight-black px-8" :href="whatsappUrl" target="_blank">
							<v-icon left>mdi-whatsapp</v-icon>
							مشاوره اختصاصی سرمایه‌گذاری
						</v-btn>
					</div>
				</div>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- 4. PRICING & PLANS SECTION (سکشن پلن و تعرفه قیمت‌ها از صفحه Home)          -->
		<!-- ========================================================================= -->
		<section id="pricing" class="landing-section pricing-hero-section">
			<!-- Background glowing orbs -->
			<div class="pricing-orb orb-p1"></div>
			<div class="pricing-orb orb-p2"></div>

			<v-container style="max-width: 1240px;" class="position-relative">
				<!-- Pricing Header -->
				<div class="text-center mb-12">
					<div class="pricing-chip mb-3">
						<v-icon small color="#ffb74d">mdi-tag-multiple-outline</v-icon>
						<span>شفاف و اقتصادی</span>
					</div>
					<h2 class="pricing-title">پلن‌ها و تعرفه قیمت‌ها</h2>
					<p class="pricing-subtitle">پلن متناسب با حجم پرونده‌های کسب‌وکار خود را انتخاب کنید</p>
				</div>

				<!-- Pricing Cards Grid -->
				<v-row class="justify-center align-stretch mb-12">
					<!-- Plan 1: Cloud Per Case -->
					<v-col cols="12" md="4">
						<div class="modern-price-card">
							<div class="price-card-header">
								<span class="plan-badge">اقتصادی</span>
								<h3 class="plan-name">پرونده‌ای سالیانه</h3>
								<div class="plan-type">سرویس‌دهی ابری (Cloud)</div>
							</div>

							<div class="price-value-box">
								<div class="price-sub">هر پرونده</div>
								<div class="price-amount">{{ cloudPrice ? cloudPrice.toString().toPriceFormat() : '...' }}</div>
								<div class="price-unit">تومان / سالانه</div>
							</div>

							<div class="plan-features-list">
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>اتصال به دامنه اختصاصی شما</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>اعتبار یکساله با پشتیبانی رایگان</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>سیستم ارسال خودکار پیامک</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>درگاه پرداخت آنلاین شاپرک</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>۱۰ روز مهلت تست رایگان</span>
								</div>
							</div>

							<div class="price-card-action">
								<v-btn v-if="isAdmin" block rounded large color="#ff9800" dark :loading="loading" @click="purchase(1)">
									ثبت خرید این پلن
								</v-btn>
								<v-btn v-else block rounded large color="#522ea8" dark @click="scrollTo('test-register')">
									تست رایگان این پلن
								</v-btn>
							</div>
						</div>
					</v-col>

					<!-- Plan 2: Unlimited Cloud (Recommended) -->
					<v-col cols="12" md="4">
						<div class="modern-price-card featured-price-card">
							<div class="featured-ribbon">
								<span>پیشنهاد ویژه • محبوب‌ترین</span>
							</div>

							<div class="price-card-header">
								<span class="plan-badge badge-featured">حرفه‌ای و بدون مرز</span>
								<h3 class="plan-name">نامحدود سالیانه</h3>
								<div class="plan-type">سرویس‌دهی ابری (Cloud)</div>
							</div>

							<div class="price-value-box">
								<div class="price-sub">تعداد پرونده نامحدود</div>
								<div class="price-amount">{{ permanentPrice ? permanentPrice.toString().toPriceFormat() : '...' }}</div>
								<div class="price-unit">تومان / سالانه</div>
							</div>

							<div class="plan-features-list">
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span><strong>بدون محدودیت</strong> در تعداد پرونده و مشتری</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>اتصال به دامنه اختصاصی فروشگاه شما</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>اعتبار یکساله با پشتیبانی VIP</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>ارسال خودکار پیامک‌های زمان‌بندی‌شده</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>درگاه بانکی پرداخت آنلاین برای مشتریان</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>۱۰ روز مهلت بررسی و تست دمو</span>
								</div>
							</div>

							<div class="price-card-action">
								<v-btn v-if="isAdmin" block rounded large color="#ff9800" dark elevation="4" :loading="loading" class="font-weight-black" @click="purchase(0)">
									خرید پلن نامحدود
								</v-btn>
								<v-btn v-else block rounded large color="#ff9800" dark elevation="4" class="font-weight-black" @click="scrollTo('test-register')">
									شروع تست رایگان پلن
								</v-btn>
							</div>
						</div>
					</v-col>

					<!-- Plan 3: Custom On-Premise -->
					<v-col cols="12" md="4">
						<div class="modern-price-card">
							<div class="price-card-header">
								<span class="plan-badge">سازمانی و دائمی</span>
								<h3 class="plan-name">دائمی سفارشی</h3>
								<div class="plan-type">نصب بر روی سرور اختصاصی</div>
							</div>

							<div class="price-value-box">
								<div class="price-sub">مالکیت دائمی نرم‌افزار</div>
								<div class="price-amount fs-h3 mt-2">تماس با فروش</div>
								<div class="price-unit">بر اساس سفارش و زیرساخت</div>
							</div>

							<div class="plan-features-list">
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>نصب روی سرور یا هاست اختصاصی شما</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>لایسنس دائمی و بدون انقضا</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>شخصی‌سازی فرم‌ها و گزارش‌ها طبق نیاز</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>پشتیبانی فنی و امنیتی اختصاصی</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>آموزش کامل مدیران و پرسنل</span>
								</div>
							</div>

							<div class="price-card-action">
								<v-btn block rounded large outlined color="#ffffff" dark :href="whatsappUrl" target="_blank">
									<v-icon left>mdi-whatsapp</v-icon>
									مشاوره خرید اختصاصی
								</v-btn>
							</div>
						</div>
					</v-col>
				</v-row>

				<!-- ========================================================================= -->
				<!-- 5. TEST DRIVE SECTION (سکشن قبل از خرید تست کنید از صفحه Home)              -->
				<!-- ========================================================================= -->
				<div id="test-register" class="test-drive-card mt-16">
					<v-row class="align-center">
						<v-col cols="12" md="6" class="pl-md-8">
							<div class="test-drive-intro">
								<div class="d-flex align-center mb-3">
									<div class="test-icon-bubble ml-3">
										<v-icon color="#522ea8">mdi-shield-airplane</v-icon>
									</div>
									<h3 class="test-title">قبل از خرید به صورت رایگان تست کنید</h3>
								</div>
								<p class="test-desc">
									جهت بررسی و تست امکانات کامل نرم‌افزار، کافی است شماره موبایل خود را در فرم مقابل وارد نمایید. بلافاصله کلمه عبور ورود به سامانه برای شما پیامک خواهد شد و می‌توانید کلیه بخش‌ها را بررسی فرمایید.
								</p>

								<div class="test-steps-list">
									<div class="step-line">
										<span class="step-num">۱</span>
										<span>شماره موبایل و نام تجاری خود را ثبت کنید.</span>
									</div>
									<div class="step-line">
										<span class="step-num">۲</span>
										<span>کلمه عبور آنی پیامک شده را دریافت کنید.</span>
									</div>
									<div class="step-line">
										<span class="step-num">۳</span>
										<span>وارد پنل شوید و دمو را با اطلاعات واقعی تست کنید.</span>
									</div>
								</div>
							</div>
						</v-col>

						<v-col cols="12" md="6">
							<div class="test-form-box">
								<v-form ref="registerForm" @submit.prevent="submitRegister">
									<v-row dense>
										<v-col cols="12">
											<v-text-field ref="regName" dark filled rounded v-model="reg.name" label="نام شخصی یا عنوان فروشگاه" prepend-inner-icon="mdi-account-outline" :error-messages="errorMessages" :rules="[(value) => !!value || 'لطفاً نام را وارد کنید']" required />
										</v-col>

										<v-col cols="12">
											<v-text-field ref="regMobile" maxlength="11" dark filled rounded v-model="reg.mobile" label="شماره موبایل (مثال: ۰۹۱۲۳۴۵۶۷۸۹)" prepend-inner-icon="mdi-cellphone" :error-messages="errorMessages" :rules="[
												(value) => !!value || 'لطفاً شماره موبایل را وارد کنید',
												(value) => isPhoneNumberValid() || 'شماره موبایل نامعتبر است'
											]" required />
										</v-col>

										<v-col cols="12" class="text-center mt-2">
											<Countdown class="white--text fs-h3 mb-2" @finish="countDownFinished" ref="countdown" />

											<v-btn v-if="sendCodeVisible" :loading="regLoading" block rounded large color="#ff9800" dark type="submit" class="font-weight-black submit-test-btn">
												<v-icon left>mdi-send-lock-outline</v-icon>
												دریافت رمز عبور پیامکی
											</v-btn>

											<div v-if="showLoginToAppBtn" class="login-prompt-box pa-4 rounded-xl mt-3">
												<div class="white--text mb-2">رمز عبور با موفقیت برای شما ارسال شد.</div>
												<v-btn block rounded large color="#ffffff" class="font-weight-black brand-purple-text" @click.prevent="loginToApp">
													<v-icon left color="#522ea8">mdi-login</v-icon>
													ورود مستقیم به نرم‌افزار
												</v-btn>
											</div>

											<div v-show="regValidation" class="error-validation-box pa-3 rounded-lg mt-3">
												<v-icon small color="white" class="ml-1">mdi-alert-circle</v-icon>
												{{ regValidation }}
											</div>
										</v-col>
									</v-row>
								</v-form>
							</div>
						</v-col>
					</v-row>
				</div>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- MODAL 1: APARAT VIDEO MODAL (جت اقساط در ۲ دقیقه)                         -->
		<!-- ========================================================================= -->
		<v-dialog v-model="videoModal" max-width="840" transition="dialog-bottom-transition">
			<v-card class="video-dialog-card rounded-2xl overflow-hidden">
				<v-card-title class="video-dialog-header d-flex justify-space-between align-center px-6 py-4">
					<div class="d-flex align-center">
						<v-icon color="#ff9800" class="ml-2">mdi-play-circle</v-icon>
						<span class="font-weight-black text-subtitle-1 white--text">معرفی جت اقساط در ۲ دقیقه</span>
					</div>
					<v-btn icon dark @click="videoModal = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<div class="video-dialog-body">
					<div class="video-dialog-frame-wrapper" v-if="videoModal">
						<iframe src="https://www.aparat.com/video/video/embed/videohash/njm27g7/vt/frame" allowFullScreen="true" webkitallowfullscreen="true" mozallowfullscreen="true" title="معرفی نرم افزار مدیریت اقساط جت اقساط"></iframe>
					</div>
				</div>

				<v-card-actions class="px-6 py-4 d-flex justify-space-between align-center bg-dialog-footer">
					<span class="caption grey--text text--lighten-1">نحوه ثبت پرونده، استعلام اعتبار و دریافت اقساط آنلاین</span>
					<v-btn text color="#ff9800" href="https://aparat.com/v/njm27g7" target="_blank">
						مشاهده در سایت آپارات
						<v-icon right small>mdi-open-in-new</v-icon>
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- ========================================================================= -->
		<!-- MODAL 2: EXCEL / LEDGER FREE MIGRATION ASSISTANT                          -->
		<!-- ========================================================================= -->
		<v-dialog v-model="excelModal" max-width="680" transition="dialog-transition">
			<v-card class="excel-dialog-card rounded-2xl overflow-hidden">
				<div class="excel-modal-banner px-6 py-5">
					<div class="d-flex align-center justify-space-between">
						<div class="d-flex align-center">
							<div class="excel-modal-icon ml-3">
								<v-icon color="#2e7d32" large>mdi-file-excel-box</v-icon>
							</div>
							<div>
								<h3 class="font-weight-black text-h6 white--text mb-1">انتقال رایگان دفتر و اکسل شما به سیستم</h3>
								<p class="caption mb-0" style="color: rgba(255, 255, 255, 0.8);">
									تیم پشتیبانی جت اقساط بدون دریافت هزینه، تمامی سوابق دفتری و فایل‌های شما را به سامانه منتقل می‌کند.
								</p>
							</div>
						</div>
						<v-btn icon dark @click="excelModal = false">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</div>
				</div>

				<v-card-text class="pa-6">
					<div class="migration-steps mb-6">
						<div class="m-step-item">
							<div class="m-step-badge">۱</div>
							<div class="m-step-info">
								<strong>ارسال فایل اکسل یا عکس صفحات دفترچه اقساط</strong>
								<p class="caption grey--text text--darken-2 mb-0">هر فرمت و ستون‌بندی که الان استفاده می‌کنید کافی است.</p>
							</div>
						</div>
						<div class="m-step-item">
							<div class="m-step-badge">۲</div>
							<div class="m-step-info">
								<strong>انطباق و دسته‌بندی اطلاعات توسط تیم فنی</strong>
								<p class="caption grey--text text--darken-2 mb-0">محاسبه دقیق مانده بدهی، اقساط وصول‌شده و تاریخ سررسیدها.</p>
							</div>
						</div>
						<div class="m-step-item">
							<div class="m-step-badge">۳</div>
							<div class="m-step-info">
								<strong>تحویل پنل آماده کار در کمتر از ۲ ساعت</strong>
								<p class="caption grey--text text--darken-2 mb-0">همراه با آموزش اختصاصی و ارسال پیامک‌های معوق قبلی.</p>
							</div>
						</div>
					</div>

					<div class="excel-cta-box pa-4 rounded-xl text-center">
						<p class="font-weight-bold text-body-2 mb-3">
							برای شروع فرآیند انتقال رایگان، کافی است در واتساپ به پشتیبان پیام دهید:
						</p>
						<v-btn block large rounded elevation="3" color="#2e7d32" dark class="font-weight-black" :href="excelWhatsappUrl" target="_blank">
							<v-icon left>mdi-whatsapp</v-icon>
							ارسال پیام در واتساپ
						</v-btn>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- ========================================================================= -->
		<!-- FLOATING QUICK WHATSAPP BUTTON WITH PULSE MICROINTERACTION                -->
		<!-- ========================================================================= -->
		<a :href="whatsappUrl" target="_blank" class="floating-whatsapp-trigger" title="پاسخگویی آنلاین در واتساپ">
			<span class="whatsapp-pulse-ring"></span>
			<span class="whatsapp-pulse-ring delay-1"></span>
			<v-icon color="#ffffff" size="32">mdi-whatsapp</v-icon>
			<span class="floating-tooltip">مشاوره رایگان واتساپ</span>
		</a>
	</div>
</template>

<script>
import { get } from 'vuex-pathify'
import api from '@/util/app-api'
import toast from '@/util/toast'
import router from '@/router'
import { getCookie } from '@/util/helpers'

export default {
	name: 'JetLandingView',

	components: {
		Countdown: () => import('@/components/Countdown'),
	},

	data: () => ({
		// Forms & Pricing state
		errorMessages: '',
		reg: { name: '', mobile: '' },
		loading: false,
		regLoading: false,
		regValidation: '',
		sendCodeVisible: true,
		showLoginToAppBtn: false,

		// Modals
		videoModal: false,
		excelModal: false,

		// WhatsApp Links
		whatsappUrl: 'https://wa.me/989361995434?text=' + encodeURIComponent('سلام، برای تست 10 روزه جت اقساط پیام دادم.'),
		excelWhatsappUrl: 'https://wa.me/989361995434?text=' + encodeURIComponent('سلام، می‌خواهم دفترچه اقساط و فایل اکسل کسب‌وکارم را به صورت رایگان به جت اقساط منتقل کنم.'),

		// 3D Parallax & Microinteraction State
		tiltX: 0,
		tiltY: 0,
		badgeX1: 0,
		badgeY1: 0,
		badgeX2: 0,
		badgeY2: 0,
		badgeX3: 0,
		badgeY3: 0,
		spotlightX: 50,
		spotlightY: 50,
	}),

	computed: {
		...get('auth', [
			'isAdmin', 'getCurrentUser'
		]),
		...get('siteConfig', [
			'permanentPrice', 'cloudPrice'
		])
	},

	mounted() {
		// Handle hash scroll on load
		if (this.$route.hash) {
			const sectionId = this.$route.hash.replace('#', '')
			this.$nextTick(() => {
				setTimeout(() => {
					this.scrollTo(sectionId)
				}, 350)
			})
		}
	},

	watch: {
		'$route.hash'(newHash) {
			if (newHash) {
				const sectionId = newHash.replace('#', '')
				this.$nextTick(() => {
					setTimeout(() => {
						this.scrollTo(sectionId)
					}, 200)
				})
			}
		}
	},

	methods: {
		// Hero 3D Card & Spotlight mousemove interaction
		onHeroMouseMove(e) {
			const banner = this.$refs.heroBanner
			if (!banner) return
			const rect = banner.getBoundingClientRect()
			const x = (e.clientX - rect.left) / rect.width - 0.5
			const y = (e.clientY - rect.top) / rect.height - 0.5

			this.tiltX = y * -10
			this.tiltY = x * 12

			this.badgeX1 = x * -24
			this.badgeY1 = y * -16

			this.badgeX2 = x * 28
			this.badgeY2 = y * 18

			this.badgeX3 = x * -18
			this.badgeY3 = y * 22

			this.spotlightX = Math.round(((e.clientX - rect.left) / rect.width) * 100)
			this.spotlightY = Math.round(((e.clientY - rect.top) / rect.height) * 100)
		},

		onHeroMouseLeave() {
			this.tiltX = 0
			this.tiltY = 0
			this.badgeX1 = 0
			this.badgeY1 = 0
			this.badgeX2 = 0
			this.badgeY2 = 0
			this.badgeX3 = 0
			this.badgeY3 = 0
			this.spotlightX = 50
			this.spotlightY = 50
		},

		// Modals
		openVideoModal() {
			this.videoModal = true
		},

		openExcelModal() {
			this.excelModal = true
		},

		// Smooth scroll to section
		scrollTo(sectionId) {
			const el = document.getElementById(sectionId)
			if (el) {
				const navOffset = 95
				const elementPosition = el.getBoundingClientRect().top
				const offsetPosition = elementPosition + window.pageYOffset - navOffset
				window.scrollTo({
					top: offsetPosition,
					behavior: 'smooth'
				})

				if (window.history && window.history.pushState) {
					window.history.pushState(null, null, `#${sectionId}`)
				}
			}
		},

		// Plan Purchase
		purchase(productType) {
			this.loading = true
			let data = {
				productType: productType,
				paymentMethod: 0
			}
			api.post('admin/purchase/create', data)
				.then(response => {
					window.location.replace(response)
				})
				.catch(err => {
					this.loading = false
					toast.error(err.message)
				})
		},

		// Submit Test-Drive Registration
		submitRegister() {
			if (!this.$refs.registerForm.validate()) { return }
			this.regValidation = ''
			this.regLoading = true

			const formData = new FormData()
			formData.append('name', this.reg.name)
			formData.append('mobile', this.fixPhoneNumber(this.reg.mobile))
			formData.append('sendSms', true)
			const affCode = getCookie('aff')
			if (affCode) {
				formData.append('affCode', affCode)
			}
			api.post('account/register', formData, { authHeader: false, contentType: 'multipart/form-data' })
				.then(() => {
					if (this.$refs.countdown && this.$refs.countdown.start) {
						this.$refs.countdown.start()
					}
					this.sendCodeVisible = false
					this.showLoginToAppBtn = true
				})
				.catch(err => {
					this.regValidation = err.message
				})
				.finally(() => {
					this.regLoading = false
				})
		},

		countDownFinished() {
			this.sendCodeVisible = true
		},

		isPhoneNumberValid() {
			return this.reg.mobile && (/^((?!(0))[0-9]{10})$/g.test(this.reg.mobile) || /^0[0-9]\d{9}$/g.test(this.reg.mobile))
		},

		loginToApp() {
			router.push('/login/0/?from=home').catch(() => { })
		},

		fixPhoneNumber(number) {
			if (number?.length === 10 && !number.startsWith('0'))
				number = '0' + number
			return number
		}
	}
}
</script>

<style lang="scss">
/* Root & Typography */
.jet-landing-root {
	font-family: "iranyekanweb", "Emkan-Regular", "Yekan", tahoma, sans-serif;
	color: #2b233a;
	overflow-x: hidden;
	background: #ffffff;
}

.landing-section {
	padding: 85px 0;
	position: relative;
}

/* ========================================================================= */
/* 1. HERO SECTION                                                           */
/* ========================================================================= */
.hero-section {
	width: 100%;
	position: relative;
	overflow: hidden;
	background: linear-gradient(145deg, #ffffff 0%, #fbf9fe 35%, #f4edfc 70%, #ffffff 100%);
	border-bottom: 1px solid rgba(82, 46, 168, 0.08);
	min-height: 640px;
	display: flex;
	align-items: center;
}

.hero-spotlight-glow {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	pointer-events: none;
	transition: background 0.15s ease-out;
}

.ambient-orb {
	position: absolute;
	border-radius: 50%;
	filter: blur(85px);
	pointer-events: none;
	opacity: 0.6;
}

.orb-1 {
	width: 450px;
	height: 450px;
	background: rgba(110, 65, 220, 0.22);
	top: -80px;
	right: -100px;
}

.orb-2 {
	width: 350px;
	height: 350px;
	background: rgba(255, 152, 0, 0.16);
	bottom: -60px;
	left: -50px;
}

.ambient-grid-pattern {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-image: radial-gradient(rgba(82, 46, 168, 0.07) 1px, transparent 1px);
	background-size: 28px 28px;
	pointer-events: none;
	opacity: 0.8;
}

/* Hero Content */
.hero-chip-badge {
	display: inline-flex;
	align-items: center;
	background: #ffffff;
	border: 1px solid rgba(255, 152, 0, 0.35);
	padding: 6px 14px;
	border-radius: 30px;
	font-size: 0.85rem;
	font-weight: 700;
	color: #522ea8;
	box-shadow: 0 4px 15px rgba(255, 152, 0, 0.12);

	.chip-pulse-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: #ff9800;
		margin-left: 6px;
		box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
		animation: pulse-orange 1.8s infinite;
	}
}

@keyframes pulse-orange {
	0% {
		transform: scale(0.95);
		box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
	}

	70% {
		transform: scale(1.05);
		box-shadow: 0 0 0 8px rgba(255, 152, 0, 0);
	}

	100% {
		transform: scale(0.95);
		box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
	}
}

.hero-main-title {
	font-size: 2.35rem;
	font-weight: 950;
	line-height: 1.45;
	color: #1e1333;
	letter-spacing: -0.5px;

	.title-highlight {
		background: linear-gradient(135deg, #522ea8 0%, #ff9800 100%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		display: inline-block;
	}
}

.hero-main-subtitle {
	font-size: 1.05rem;
	line-height: 2.1;
	color: #4b3e64;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;

	.sub-feature-pill {
		display: inline-flex;
		align-items: center;
		background: #ffffff;
		padding: 3px 10px;
		border-radius: 8px;
		font-weight: 700;
		color: #2b1f42;
		border: 1px solid rgba(82, 46, 168, 0.12);
		margin: 2px 0;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
		width: 230px;
	}

	.sub-separator {
		display: flex;
		margin: 0 5px;
		color: #ff9800;
		font-weight: 900;
		height: 20px;
		justify-content: center;
		align-items: center;
	}
}

/* 3 CTA Buttons */
.hero-cta-buttons {
	.cta-btn {
		font-family: inherit;
		font-weight: 800;
		letter-spacing: 0;
		transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

		.btn-text {
			font-size: 0.95rem;
		}

		&:hover {
			transform: translateY(-3px);
		}
	}

	.btn-whatsapp {
		width: 100%;
		height: 52px !important;
		background: linear-gradient(135deg, #25d366 0%, #128c7e 100%) !important;
		color: #ffffff !important;
		box-shadow: 0 8px 24px rgba(37, 211, 102, 0.35) !important;
		position: relative;
		overflow: hidden;

		&:hover {
			box-shadow: 0 12px 30px rgba(37, 211, 102, 0.5) !important;
		}
	}

	.btn-video {
		height: 48px !important;
		background: #ffffff !important;
		color: #2b1f42 !important;
		border: 1px solid rgba(255, 152, 0, 0.4) !important;
		box-shadow: 0 4px 15px rgba(255, 152, 0, 0.15) !important;

		&:hover {
			background: #fff8f0 !important;
			border-color: #ff9800 !important;
		}
	}

	.btn-excel {
		height: 48px !important;
		border: 1.5px solid #2e7d32 !important;
		color: #2e7d32 !important;
		background: #ffffff !important;

		&:hover {
			background: #e8f5e9 !important;
			color: #1b5e20 !important;
		}
	}
}

.hero-stats-row {
	display: flex;
	flex-wrap: wrap;
	gap: 12px;

	.stat-pill {
		display: flex;
		align-items: center;
		gap: 6px;
		background: rgba(255, 255, 255, 0.85);
		border: 1px solid rgba(82, 46, 168, 0.1);
		padding: 6px 12px;
		border-radius: 20px;
		font-size: 0.82rem;
		font-weight: 700;
		color: #4a3c63;
	}
}

/* 3D Mockup Visual */
.hero-mockup-wrapper {
	position: relative;
	perspective: 1200px;
	padding: 20px;
}

.hero-3d-card {
	position: relative;
	border-radius: 24px;
	background: #170e2c;
	box-shadow: 0 25px 60px rgba(43, 23, 84, 0.28), 0 0 0 1px rgba(255, 255, 255, 0.12);
	overflow: hidden;
	transition: transform 0.15s ease-out;
	will-change: transform;
}

.mockup-header-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 18px;
	background: rgba(255, 255, 255, 0.05);
	border-bottom: 1px solid rgba(255, 255, 255, 0.08);

	.mockup-dot-group {
		display: flex;
		gap: 6px;

		.m-dot {
			width: 10px;
			height: 10px;
			border-radius: 50%;
		}

		.dot-red {
			background: #ff5f56;
		}

		.dot-yellow {
			background: #ffbd2e;
		}

		.dot-green {
			background: #27c93f;
		}
	}

	.mockup-title-text {
		color: rgba(255, 255, 255, 0.9);
		font-size: 0.78rem;
		font-weight: 700;
		display: flex;
		align-items: center;
	}

	.mockup-live-status {
		display: flex;
		align-items: center;
		gap: 5px;

		.live-dot {
			width: 6px;
			height: 6px;
			border-radius: 50%;
			background: #4caf50;
		}

		.live-label {
			font-size: 0.7rem;
			color: #a5d6a7;
		}
	}
}

.mockup-image-container {
	position: relative;
	overflow: hidden;

	.mockup-screen-img {
		width: 100%;
		height: auto;
		display: block;
		object-fit: cover;
	}

	.mockup-sheen {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		pointer-events: none;
	}

	.mockup-floating-pill {
		position: absolute;
		bottom: 14px;
		right: 14px;
		background: rgba(255, 255, 255, 0.92);
		backdrop-filter: blur(8px);
		padding: 6px 12px;
		border-radius: 20px;
		font-size: 0.78rem;
		font-weight: 800;
		color: #1b5e20;
		box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
		display: flex;
		align-items: center;
	}
}

/* Floating Parallax Badges */
.floating-badge {
	position: absolute;
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(12px);
	border: 1px solid rgba(82, 46, 168, 0.14);
	box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
	border-radius: 16px;
	padding: 10px 14px;
	display: flex;
	align-items: center;
	gap: 10px;
	z-index: 5;
	transition: transform 0.15s ease-out;

	.badge-icon-box {
		width: 36px;
		height: 36px;
		border-radius: 10px;
		display: flex;
		align-items: center;
		justify-content: center;

		&.bg-purple {
			background: #522ea8;
		}

		&.bg-emerald {
			background: #2e7d32;
		}

		&.bg-orange {
			background: #ff9800;
		}
	}

	.badge-body {
		display: flex;
		flex-direction: column;

		.badge-bold {
			font-size: 0.82rem;
			font-weight: 900;
			color: #1f1435;
		}

		.badge-sub {
			font-size: 0.72rem;
			color: #6e6482;
		}
	}
}

.badge-credit {
	top: -5px;
	left: -5px;
}

.badge-sign {
	bottom: -15px;
	right: 15px;
}

.badge-sms {
	bottom: 25px;
	left: -15px;
}

/* ========================================================================= */
/* 2. FOUR BENEFITS COLUMNS (زبان صنف)                                        */
/* ========================================================================= */
.benefits-bazaar-section {
	background: #fbf9ff;
}

.section-badge-header {
	.section-chip {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: #ffffff;
		border: 1px solid rgba(255, 152, 0, 0.3);
		padding: 5px 14px;
		border-radius: 20px;
		font-size: 0.84rem;
		font-weight: 700;
		color: #e65100;
	}

	.section-main-heading {
		font-size: 2rem;
		font-weight: 900;
		color: #22143d;
		margin-top: 6px;
		margin-bottom: 8px;
	}

	.section-sub-heading {
		font-size: 1rem;
		color: #6c5f84;
		max-width: 650px;
		margin: 0 auto;
	}
}

.bazaar-column-card {
	background: #ffffff;
	border-radius: 22px;
	padding: 28px 22px;
	height: 100%;
	display: flex;
	flex-direction: column;
	border: 1px solid rgba(82, 46, 168, 0.1);
	box-shadow: 0 8px 24px rgba(82, 46, 168, 0.05);
	position: relative;
	transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);

	&:hover {
		transform: translateY(-8px);
		box-shadow: 0 18px 40px rgba(82, 46, 168, 0.12);
	}

	.stage-top-indicator {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 18px;

		.stage-step-num {
			width: 28px;
			height: 28px;
			border-radius: 50%;
			background: #f2ecfb;
			color: #522ea8;
			display: flex;
			align-items: center;
			justify-content: center;
			font-weight: 900;
			font-size: 0.85rem;
		}

		.stage-pill-tag {
			font-size: 0.78rem;
			font-weight: 800;
			padding: 3px 10px;
			border-radius: 12px;
			background: #f4f2f8;
			color: #522ea8;
		}
	}

	.stage-icon-circle {
		width: 64px;
		height: 64px;
		border-radius: 18px;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 18px;

		&.bg-stage-1 {
			background: #ffebee;
		}

		&.bg-stage-2 {
			background: #ede7f6;
		}

		&.bg-stage-3 {
			background: #fff3e0;
		}

		&.bg-stage-4 {
			background: #e8f5e9;
		}
	}

	.stage-card-title {
		font-size: 1.25rem;
		font-weight: 900;
		color: #1f1435;
		margin-bottom: 8px;
	}

	.stage-bazaar-slogan {
		font-size: 0.94rem;
		font-weight: 800;
		line-height: 1.75;
		color: #3b2a57;
		background: #fbf9ff;
		padding: 10px 12px;
		border-radius: 12px;
		border-right: 3px solid #ff9800;
		margin-bottom: 18px;
	}

	.stage-features-list {
		display: flex;
		flex-direction: column;
		gap: 10px;
		flex-grow: 1;
		margin-bottom: 20px;

		.stage-feature-item {
			display: flex;
			align-items: flex-start;
			gap: 8px;
			font-size: 0.84rem;
			line-height: 1.7;
			color: #52476b;
		}
	}

	.stage-card-footer {
		margin-top: auto;
		border-top: 1px solid rgba(0, 0, 0, 0.05);
		padding-top: 12px;
	}
}

/* ========================================================================= */
/* 3. SPONSOR / INVESTOR SECTION                                             */
/* ========================================================================= */
.sponsor-vip-section {
	background: radial-gradient(ellipse at 50% 20%, #1e1037 0%, #0d061a 100%);
	color: #ffffff;
	overflow: hidden;
}

.sponsor-orb {
	position: absolute;
	border-radius: 50%;
	filter: blur(100px);
	pointer-events: none;
	opacity: 0.45;
}

.orb-gold {
	width: 400px;
	height: 400px;
	background: rgba(255, 183, 77, 0.25);
	top: -50px;
	right: 10%;
}

.orb-dark-purple {
	width: 500px;
	height: 500px;
	background: rgba(110, 65, 220, 0.35);
	bottom: -100px;
	left: 5%;
}

.sponsor-header {
	.sponsor-chip {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: rgba(255, 255, 255, 0.08);
		border: 1px solid rgba(255, 183, 77, 0.3);
		padding: 5px 14px;
		border-radius: 20px;
		font-size: 0.84rem;
		font-weight: 700;
		color: #ffb74d;
	}

	.sponsor-main-title {
		font-size: 2.2rem;
		font-weight: 950;
		color: #ffffff;
		margin-top: 8px;
		margin-bottom: 8px;
		letter-spacing: -0.5px;
	}

	.sponsor-sub-title {
		font-size: 1rem;
		color: rgba(255, 255, 255, 0.7);
		max-width: 680px;
		margin: 0 auto;
	}
}

.sponsor-feature-card {
	background: rgba(255, 255, 255, 0.04);
	backdrop-filter: blur(14px);
	-webkit-backdrop-filter: blur(14px);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 24px;
	padding: 32px 24px;
	height: 100%;
	display: flex;
	flex-direction: column;
	position: relative;
	transition: all 0.35s ease;

	&:hover {
		transform: translateY(-6px);
		border-color: rgba(255, 183, 77, 0.4);
		box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
		background: rgba(255, 255, 255, 0.07);
	}

	&.featured-sponsor-card {
		border: 1.5px solid rgba(255, 152, 0, 0.5);
		background: rgba(255, 152, 0, 0.06);

		.featured-tag {
			position: absolute;
			top: -12px;
			left: 20px;
			background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
			color: #ffffff;
			font-size: 0.72rem;
			font-weight: 900;
			padding: 3px 12px;
			border-radius: 20px;
			box-shadow: 0 4px 10px rgba(245, 124, 0, 0.4);
		}
	}

	.sponsor-icon-wrapper {
		width: 60px;
		height: 60px;
		border-radius: 16px;
		background: rgba(255, 255, 255, 0.06);
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 20px;
	}

	.sponsor-card-heading {
		font-size: 1.3rem;
		font-weight: 900;
		color: #ffffff;
		margin-bottom: 12px;
	}

	.sponsor-card-desc {
		font-size: 0.92rem;
		line-height: 1.85;
		color: rgba(255, 255, 255, 0.75);
		margin-bottom: 20px;
	}

	.sponsor-bullet-features {
		display: flex;
		flex-direction: column;
		gap: 10px;
		margin-top: auto;
		border-top: 1px solid rgba(255, 255, 255, 0.08);
		padding-top: 16px;

		.b-line {
			display: flex;
			align-items: center;
			gap: 8px;
			font-size: 0.85rem;
			color: rgba(255, 255, 255, 0.9);
		}
	}
}

.sponsor-cta-banner {
	background: rgba(255, 255, 255, 0.06);
	backdrop-filter: blur(12px);
	border: 1px solid rgba(255, 183, 77, 0.25);
	border-radius: 20px;
}

/* ========================================================================= */
/* 4. PRICING & TEST DRIVE SECTION                                           */
/* ========================================================================= */
.pricing-hero-section {
	background: radial-gradient(ellipse at 50% 30%, #20133b 0%, #0c0617 100%);
	color: #ffffff;
	overflow: hidden;
}

.pricing-orb {
	position: absolute;
	border-radius: 50%;
	filter: blur(90px);
	pointer-events: none;
	opacity: 0.5;
}

.orb-p1 {
	width: 400px;
	height: 400px;
	background: rgba(110, 65, 220, 0.35);
	top: -50px;
	right: -80px;
}

.orb-p2 {
	width: 350px;
	height: 350px;
	background: rgba(255, 152, 0, 0.2);
	bottom: -40px;
	left: -50px;
}

.pricing-chip {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background: rgba(255, 255, 255, 0.08);
	border: 1px solid rgba(255, 183, 77, 0.3);
	padding: 5px 14px;
	border-radius: 20px;
	font-size: 0.84rem;
	font-weight: 700;
	color: #ffb74d;
}

.pricing-title {
	font-size: 2.2rem;
	font-weight: 900;
	color: #ffffff;
	margin-top: 6px;
	margin-bottom: 6px;
}

.pricing-subtitle {
	font-size: 1rem;
	color: rgba(255, 255, 255, 0.7);
}

.modern-price-card {
	background: rgba(255, 255, 255, 0.05);
	backdrop-filter: blur(12px);
	-webkit-backdrop-filter: blur(12px);
	border: 1px solid rgba(255, 255, 255, 0.12);
	border-radius: 24px;
	padding: 32px 24px;
	height: 100%;
	display: flex;
	flex-direction: column;
	position: relative;
	transition: all 0.3s ease;

	&:hover {
		transform: translateY(-6px);
		border-color: rgba(110, 65, 220, 0.45);
		box-shadow: 0 15px 40px rgba(0, 0, 0, 0.35);
	}

	&.featured-price-card {
		border: 2px solid #ff9800;
		background: rgba(255, 152, 0, 0.07);
		transform: scale(1.02);

		&:hover {
			transform: scale(1.02) translateY(-6px);
		}

		.featured-ribbon {
			position: absolute;
			top: -14px;
			right: 24px;
			background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
			color: #ffffff;
			font-size: 0.76rem;
			font-weight: 900;
			padding: 4px 14px;
			border-radius: 20px;
			box-shadow: 0 4px 12px rgba(245, 124, 0, 0.4);
		}
	}

	.plan-badge {
		font-size: 0.75rem;
		font-weight: 800;
		background: rgba(255, 255, 255, 0.1);
		color: #ffb74d;
		padding: 4px 10px;
		border-radius: 12px;
		display: inline-block;
		margin-bottom: 12px;

		&.badge-featured {
			background: #ff9800;
			color: #ffffff;
		}
	}

	.plan-name {
		font-size: 1.4rem;
		font-weight: 900;
		color: #ffffff;
		margin-bottom: 4px;
	}

	.plan-type {
		font-size: 0.84rem;
		color: rgba(255, 255, 255, 0.6);
	}

	.price-value-box {
		margin: 24px 0;
		padding: 16px;
		border-radius: 16px;
		background: rgba(0, 0, 0, 0.2);
		text-align: center;

		.price-sub {
			font-size: 0.82rem;
			color: rgba(255, 255, 255, 0.65);
		}

		.price-amount {
			font-size: 2.2rem;
			font-weight: 900;
			color: #ffffff;
			font-family: "yekan", sans-serif;
		}

		.price-unit {
			font-size: 0.82rem;
			color: rgba(255, 255, 255, 0.65);
		}
	}

	.plan-features-list {
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin-bottom: 28px;
		flex-grow: 1;

		.feature-line {
			display: flex;
			align-items: center;
			gap: 10px;
			font-size: 0.88rem;
			color: rgba(255, 255, 255, 0.88);
		}
	}
}

/* Test Drive Box */
.test-drive-card {
	background: rgba(255, 255, 255, 0.05);
	backdrop-filter: blur(14px);
	-webkit-backdrop-filter: blur(14px);
	border-radius: 28px;
	border: 1px solid rgba(255, 255, 255, 0.14);
	padding: 36px 30px;
	box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);

	.test-icon-bubble {
		width: 48px;
		height: 48px;
		border-radius: 14px;
		background: #ffffff;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.test-title {
		font-size: 1.35rem;
		font-weight: 900;
		color: #ffffff;
	}

	.test-desc {
		font-size: 0.95rem;
		color: rgba(255, 255, 255, 0.75);
		line-height: 1.9;
	}

	.test-steps-list {
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin-top: 18px;

		.step-line {
			display: flex;
			align-items: center;
			gap: 12px;
			font-size: 0.9rem;
			color: rgba(255, 255, 255, 0.9);

			.step-num {
				width: 24px;
				height: 24px;
				border-radius: 50%;
				background: #ff9800;
				color: #ffffff;
				display: flex;
				align-items: center;
				justify-content: center;
				font-weight: 800;
				font-size: 0.8rem;
				flex-shrink: 0;
			}
		}
	}

	.test-form-box {
		.v-input__slot {
			background: rgba(14, 7, 28, 0.6) !important;
			border: 1px solid rgba(255, 255, 255, 0.12) !important;
			transition: border-color 0.25s ease;

			&:hover {
				border-color: rgba(110, 65, 220, 0.5) !important;
			}
		}

		.submit-test-btn {
			background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%) !important;
			box-shadow: 0 6px 20px rgba(245, 124, 0, 0.35) !important;
		}

		.login-prompt-box {
			background: rgba(82, 46, 168, 0.4);
			border: 1px solid rgba(255, 255, 255, 0.2);

			.brand-purple-text {
				color: #522ea8 !important;
			}
		}

		.error-validation-box {
			background: rgba(233, 30, 99, 0.8);
			color: #ffffff;
			font-size: 0.88rem;
		}
	}
}

/* ========================================================================= */
/* MODALS STYLING                                                            */
/* ========================================================================= */
.video-dialog-card {
	background: #170e2c !important;
	border: 1px solid rgba(255, 255, 255, 0.15) !important;

	.video-dialog-header {
		background: rgba(0, 0, 0, 0.3);
		border-bottom: 1px solid rgba(255, 255, 255, 0.08);
	}

	.video-dialog-body {
		padding: 0;
		background: #000000;

		.video-dialog-frame-wrapper {
			position: relative;
			width: 100%;
			padding-bottom: 56.25%;
			/* 16:9 Aspect Ratio */
			height: 0;

			iframe {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				border: none;
			}
		}
	}

	.bg-dialog-footer {
		background: rgba(0, 0, 0, 0.3);
		border-top: 1px solid rgba(255, 255, 255, 0.08);
	}
}

.excel-dialog-card {
	background: #ffffff !important;

	.excel-modal-banner {
		background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%);

		.excel-modal-icon {
			width: 48px;
			height: 48px;
			border-radius: 12px;
			background: #ffffff;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
		}
	}

	.migration-steps {
		display: flex;
		flex-direction: column;
		gap: 16px;

		.m-step-item {
			display: flex;
			align-items: flex-start;
			gap: 14px;

			.m-step-badge {
				width: 32px;
				height: 32px;
				border-radius: 50%;
				background: #e8f5e9;
				color: #2e7d32;
				display: flex;
				align-items: center;
				justify-content: center;
				font-weight: 900;
				font-size: 0.95rem;
				flex-shrink: 0;
			}
		}
	}

	.excel-cta-box {
		background: #f1f8e9;
		border: 1px dashed #81c784;
	}
}

/* ========================================================================= */
/* FLOATING WHATSAPP TRIGGER                                                 */
/* ========================================================================= */
.floating-whatsapp-trigger {
	position: fixed;
	bottom: 28px;
	left: 28px;
	width: 60px;
	height: 60px;
	border-radius: 50%;
	background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 10px 30px rgba(37, 211, 102, 0.45);
	z-index: 999;
	text-decoration: none;
	transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

	&:hover {
		transform: scale(1.1) rotate(5deg);
		box-shadow: 0 15px 35px rgba(37, 211, 102, 0.6);

		.floating-tooltip {
			opacity: 1;
			visibility: visible;
			transform: translateX(0);
		}
	}

	.whatsapp-pulse-ring {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		border-radius: 50%;
		border: 2px solid #25d366;
		animation: pulse-ring 2.2s infinite cubic-bezier(0.215, 0.61, 0.355, 1);
		pointer-events: none;

		&.delay-1 {
			animation-delay: 0.7s;
		}
	}

	.floating-tooltip {
		position: absolute;
		right: 70px;
		background: #1f1435;
		color: #ffffff;
		padding: 6px 14px;
		border-radius: 20px;
		font-size: 0.8rem;
		font-weight: 700;
		white-space: nowrap;
		box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
		opacity: 0;
		visibility: hidden;
		transform: translateX(-10px);
		transition: all 0.25s ease;
		pointer-events: none;

		&::after {
			content: '';
			position: absolute;
			left: 100%;
			top: 50%;
			margin-top: -5px;
			border-width: 5px;
			border-style: solid;
			border-color: transparent transparent transparent #1f1435;
		}
	}
}

@keyframes pulse-ring {
	0% {
		transform: scale(0.95);
		opacity: 0.8;
	}

	50% {
		opacity: 0.4;
	}

	100% {
		transform: scale(1.6);
		opacity: 0;
	}
}

/* Responsive Styles */
@media (max-width: 959px) {
	.hero-main-title {
		font-size: 1.85rem;
	}

	.hero-section {
		padding: 30px 0;
	}

	.floating-badge {
		display: none;
		/* Hide floating badges on mobile to keep layout clean */
	}

	.floating-whatsapp-trigger {
		bottom: 20px;
		left: 20px;
		width: 52px;
		height: 52px;
	}
}
</style>
